import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { LocacaoDto } from "@/services/locacoes";

interface Props {
  open: boolean;
  locacao: LocacaoDto | null;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (values: { horimetroRetorno: number; odometroRetorno: number; dataFimReal: string }) => Promise<void>;
  onMutationComplete?: () => void | Promise<void>;
}

export function ModalEncerrarLocacao({ open, locacao, busy, onClose, onConfirm, onMutationComplete }: Props) {
  const [horimetroRetorno, setHorimetroRetorno] = useState("");
  const [odometroRetorno, setOdometroRetorno] = useState("");
  const [dataFimReal, setDataFimReal] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && locacao) {
      const sugerido = locacao.horimetroAtual ?? locacao.horimetroSaida;
      setHorimetroRetorno(String(sugerido));
      setOdometroRetorno(String(locacao.odometroSaida));
      setDataFimReal(new Date().toISOString().slice(0, 10));
      setError(null);
    }
  }, [open, locacao]);

  const handleSubmit = async () => {
    setError(null);
    if (!locacao) return;

    const h = Number(horimetroRetorno);
    const o = Number(odometroRetorno);
    if (!Number.isFinite(h) || h < locacao.horimetroSaida) {
      setError(`Horímetro de retorno deve ser ≥ ${locacao.horimetroSaida}.`);
      return;
    }
    if (!Number.isFinite(o) || o < locacao.odometroSaida) {
      setError(`Odômetro de retorno deve ser ≥ ${locacao.odometroSaida} km.`);
      return;
    }

    try {
      await onConfirm({
        horimetroRetorno: h,
        odometroRetorno: o,
        dataFimReal,
      });
      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : "Erro ao encerrar locação.");
      setError(msg);
    }
  };

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      </div>
      <span className="font-bold text-slate-900">Encerrar locação</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} loading={busy}>
            Confirmar retorno
          </Button>
        </div>
      }
    >
      {locacao && (
        <div className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
          )}
          <p className="text-sm text-slate-600">
            <span className="font-semibold">{locacao.maquinaNome}</span> — saída locação: horímetro{" "}
            <strong>{locacao.horimetroSaida}</strong>, atual{" "}
            <strong>{locacao.horimetroAtual ?? locacao.horimetroSaida}</strong>, odômetro{" "}
            <strong>{locacao.odometroSaida}</strong> km
          </p>
          <Input
            label="Horímetro retorno *"
            type="number"
            min={locacao.horimetroSaida}
            step="0.1"
            value={horimetroRetorno}
            onChange={(e) => setHorimetroRetorno(e.target.value)}
          />
          <Input
            label="Odômetro retorno (km) *"
            type="number"
            min={locacao.odometroSaida}
            step="0.1"
            value={odometroRetorno}
            onChange={(e) => setOdometroRetorno(e.target.value)}
          />
          <Input
            label="Data retorno"
            type="date"
            value={dataFimReal}
            onChange={(e) => setDataFimReal(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}
